/**
 * /tenants/$slug/authentication/authenticators — the Authenticators tab.
 *
 * Pairs two distinct-RBAC sections that together answer "which authenticators,
 * and how much do we trust them":
 *   1. Authenticator allowlist (auth_methods.manage) — which AAGUIDs may register.
 *   2. Identity trust policy (tenant_settings.manage) — the attestation / enrollment /
 *      recovery trust matrix; folded in from the former standalone "Identity trust" tab
 *      during the 6→3 hub composition. Each section keeps its own permission gate.
 */

import {
  createFileRoute,
  getRouteApi,
  Outlet,
  useChildMatches,
} from "@tanstack/react-router";
import { queryKeys } from "@/lib/query-keys";
import { fetchAllowlist } from "@/features/authenticator-allowlist/queries";
import { AuthenticatorAllowlistSection } from "@/features/authenticator-allowlist/pages/authenticator-allowlist-page";
import { IdentityTrustSettingsPage } from "@/features/identity-trust-settings/pages/identity-trust-settings-page";

export const Route = createFileRoute(
  "/_authed/tenants/$slug/authentication/authenticators",
)({
  loader: async ({ context }) => {
    const tenant = context.tenant;
    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.authenticatorAllowlist.list(tenant.id),
      queryFn: () => fetchAllowlist(tenant.id),
    });
  },
  component: AuthenticatorsTab,
});

const tenantRouteApi = getRouteApi("/_authed/tenants/$slug");

function AuthenticatorsTab() {
  const childMatches = useChildMatches();
  const { slug } = Route.useParams();
  const { tenant } = tenantRouteApi.useRouteContext();
  // This route pairs a component (the tab) with a full-page child route
  // (/new — the "Approve an authenticator" create form). A
  // component-bearing parent MUST render <Outlet/> or its children have
  // nowhere to appear — the bug where /authenticators/new showed the
  // tab with no form fields and fired no request. Render ONLY the
  // outlet when a child is active so the create page replaces the tab;
  // otherwise render the tab itself.
  if (childMatches.length > 0) return <Outlet />;
  return (
    <div className="space-y-8">
      <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
        By default any FIDO2 authenticator can register, and the identity-trust matrix is
        already active with secure defaults. Use the allowlist to restrict registration to
        specific hardware; tune the matrix only for stricter enrollment or recovery rules.
      </p>
      <AuthenticatorAllowlistSection slug={slug} tenantID={tenant.id} />
      <IdentityTrustSettingsPage />
    </div>
  );
}
